export const getBookingStatusBadgeMeta = (booking) => {
  const status = String(booking?.booking_status || "").toLowerCase();
  const label = booking?.status_name || "Pending";

  let badgeClass = "bg-warning";
  if (status === "completed" || status === "confirmed") badgeClass = "bg-success";
  else if (status === "in_progress" || status === "ongoing" || status === "assigned") {
    badgeClass = "bg-info";
  } else if (status === "accepted") badgeClass = "bg-primary";
  else if (status === "requested" || status === "pending") badgeClass = "bg-warning";
  else if (status === "inquiry") badgeClass = "bg-secondary";
  else if (status === "cancelled") badgeClass = "bg-danger";

  return { label, badgeClass };
};

export const formatMoney = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return "0";
  return n.toFixed(2).replace(/\.00$/, "");
};

export const round2 = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100) / 100;
};

export const getRefundStatusMeta = (status) => {
  const key = String(status || "").toLowerCase();
  if (["success", "manual_done"].includes(key)) {
    return { label: "Refunded", badgeClass: "bg-success", textClass: "text-success" };
  }
  if (["failed"].includes(key)) {
    return { label: "Refund Failed", badgeClass: "bg-danger", textClass: "text-danger" };
  }
  if (["initiated", "processing", "manual_pending"].includes(key)) {
    return { label: "Refund In Progress", badgeClass: "bg-warning", textClass: "text-warning" };
  }
  return { label: "No Refund", badgeClass: "bg-secondary", textClass: "text-muted" };
};

export const normalizeRefundRecord = (refund) =>
  refund && Number(refund.refund_amount ?? 0) > 0 ? refund : null;

export const resolveBookingLevelRefund = (refunds = []) => {
  if (!Array.isArray(refunds) || refunds.length === 0) return null;
  const bookingRefund = refunds.find(
    (row) => !row?.booking_ride_id && Number(row?.refund_amount ?? 0) > 0,
  );
  return normalizeRefundRecord(bookingRefund);
};

export const isRideCancelledStatus = (ride) => {
  const statusName = String(ride?.status_name || "").toLowerCase();
  const statusCode = Number(ride?.status);
  return statusName.includes("cancel") || statusCode === 4;
};

export const countCancelledRides = (rides = []) =>
  rides.filter(isRideCancelledStatus).length;

export const isFullBookingCancelled = (booking, rides = []) => {
  if (!Array.isArray(rides) || rides.length === 0) return false;
  const bookingCancelled =
    String(booking?.booking_status || "").toLowerCase() === "cancelled" ||
    String(booking?.status_name || "").toLowerCase().includes("cancel");
  return bookingCancelled && countCancelledRides(rides) === rides.length;
};

export const splitRefundAmount = (totalAmount, parts, index) => {
  const total = Number(totalAmount);
  if (!Number.isFinite(total) || total <= 0 || parts <= 0 || index < 0 || index >= parts) {
    return 0;
  }
  const share = round2(total / parts);
  if (index === parts - 1) {
    return round2(total - share * (parts - 1));
  }
  return share;
};

export const resolveRideRefundForDisplay = ({
  rideRefund = null,
  bookingLevelRefund = null,
  isRideCancelled = false,
  attachBookingLevelRefund = false,
  splitRefund = null,
}) => {
  const rideLevel = normalizeRefundRecord(rideRefund);
  if (rideLevel) {
    return { refund: rideLevel, amountOverride: null };
  }

  if (!isRideCancelled || !bookingLevelRefund) {
    return { refund: null, amountOverride: null };
  }

  if (splitRefund && splitRefund.parts > 1) {
    return {
      refund: bookingLevelRefund,
      amountOverride: splitRefundAmount(
        bookingLevelRefund.refund_amount,
        splitRefund.parts,
        splitRefund.index,
      ),
    };
  }

  if (attachBookingLevelRefund) {
    return { refund: bookingLevelRefund, amountOverride: null };
  }

  return { refund: null, amountOverride: null };
};

export const getCancelledRideBadgeMeta = (refundForStatus) => {
  if (refundForStatus) {
    return getRefundStatusMeta(refundForStatus.status);
  }
  return { label: "Cancelled", badgeClass: "bg-danger", textClass: "text-danger" };
};

export const buildRefundStatusStep = (refund, amountOverride = null) => {
  if (!refund) return null;
  const meta = getRefundStatusMeta(refund.status);
  const processedAt = refund?.processed_at ? new Date(refund.processed_at) : null;
  const when =
    processedAt && !Number.isNaN(processedAt.getTime())
      ? `${processedAt.getDate()} ${processedAt.toLocaleDateString("en-US", { month: "short" })} - ${processedAt.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })}`
      : "-";

  const displayAmount =
    amountOverride !== null && amountOverride !== undefined
      ? amountOverride
      : (refund?.refund_amount ?? 0);

  if (Number(displayAmount) <= 0) {
    return null;
  }

  return {
    key: `refund-step-${refund?.id ?? "na"}`,
    label: `${meta.label}`,
    badge: meta.badgeClass,
    when,
    location: refund?.failure_reason
      ? `Reason: ${refund.failure_reason}`
      : `Amount: ₹${formatMoney(displayAmount)}`,
  };
};

/** Human-readable km for trip summary (checkout + booking detail). */
export const formatDistanceKmForDisplay = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n >= 100) return String(Math.round(n));
  const x = Math.round(n * 10) / 10;
  return Number.isInteger(x) ? String(x) : String(x.toFixed(1)).replace(/\.0$/, "");
};

/** Onward / first-leg distance (km) from checkout session or API quote. */
export const resolveOnwardDistanceKm = (bookingData) => {
  if (!bookingData || typeof bookingData !== "object") return null;
  const fd = bookingData.fare_details;
  const candidates = [
    bookingData.distance,
    bookingData.distance_km,
    fd?.distance_km,
    bookingData.rides?.[0]?.distance_km,
    bookingData.trips?.[0]?.distance_km,
  ];
  for (const c of candidates) {
    if (c != null && c !== "" && Number.isFinite(Number(c))) {
      return Number(c);
    }
  }
  return null;
};

/** Return-leg distance (km) when booking_type is round trip. */
export const resolveReturnDistanceKm = (bookingData) => {
  if (!bookingData || typeof bookingData !== "object") return null;
  const fd = bookingData.fare_details;
  const candidates = [
    bookingData.return_distance,
    bookingData.return_distance_km,
    fd?.return_distance_km,
    bookingData.rides?.[1]?.distance_km,
  ];
  for (const c of candidates) {
    if (c != null && c !== "" && Number.isFinite(Number(c))) {
      return Number(c);
    }
  }
  return null;
};
